import { Link, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  FileEdit, 
  Settings, 
  LogOut,
  UsersRound,
  HelpCircle,
  Inbox,
  Contact,
  Mail,
  Zap,
  BarChart3,
  Sparkles,
} from "lucide-react";
import Logo from "./Logo";

export default function AppSidebar({ user, onLogout }) {
  const location = useLocation();
  const currentPath = location.pathname;

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/submissions", label: "Submissions", icon: Inbox },
    { path: "/contacts", label: "Contacts", icon: Contact },
    { path: "/form-builder", label: "Form Builder", icon: FileEdit },
    { path: "/email-templates", label: "Email Templates", icon: Mail },
    { path: "/automations", label: "Automations", icon: Zap },
    { path: "/analytics", label: "Analytics", icon: BarChart3 },
    { path: "/team", label: "Team", icon: UsersRound },
    { path: "/settings", label: "Settings", icon: Settings },
    { path: "/support", label: "Support", icon: HelpCircle },
  ];

  // Lead Intelligence is now workspace-scoped via the `entitlements` flag.
  // Platform admins always see it. Other workspaces only see it when the
  // entitlement has been granted from the admin panel.
  const hasLeadIntel =
    user?.is_admin === true
    || (user?.entitlements && user.entitlements.lead_intelligence === true);

  const adminItems = [];
  if (hasLeadIntel) {
    adminItems.push({ path: "/lead-intelligence", label: "Lead Intelligence", icon: Sparkles });
  }
  if (user?.is_admin) {
    adminItems.push({ path: "/admin/lead-intelligence", label: "LI Admin", icon: Sparkles, admin: true });
  }

  return (
    <aside className="w-64 bg-zinc-950 flex flex-col fixed h-screen" data-testid="app-sidebar">
      <div className="px-6 py-5 border-b border-zinc-800/60">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={32} className="text-white" />
          <span className="text-lg font-bold text-white" style={{ fontFamily: "Inter" }}>HeyIntake</span>
        </Link>
      </div>
      
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <ul className="space-y-0.5">
          {navItems.map((item) => {
            const isActive = currentPath === item.path || (item.path !== "/dashboard" && currentPath.startsWith(item.path));
            return (
              <li key={item.path}>
                <Link 
                  to={item.path} 
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                    isActive 
                      ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/20" 
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2 : 1.5} />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
          {adminItems.length > 0 && (
            <>
              <li className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-zinc-600 font-semibold">Admin</li>
              {adminItems.map((item) => {
                const isActive = currentPath.startsWith(item.path);
                return (
                  <li key={item.path}>
                    <Link 
                      to={item.path} 
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                        isActive 
                          ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/20" 
                          : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2 : 1.5} />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </>
          )}
        </ul>
      </nav>
      
      <div className="px-3 py-4 border-t border-zinc-800/60">
        <button
          onClick={onLogout}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/60 transition-all duration-150 w-full text-[13px]"
          data-testid="nav-logout"
        >
          <LogOut className="w-[18px] h-[18px]" strokeWidth={1.5} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
